import React from 'react';
import { Eye, Edit2, Trash2, ImageIcon, Book, Calendar, Users, FileText, HelpCircle, GitFork, ClipboardList, Shield, Tag, Link2 } from 'lucide-react';

const MobileView = ({
  items: rawItems,
  activeTab,
  onView,
  onEdit,
  onDelete,
  getThumbnailUrl,
  truncateText,
  getItemTitle,
  isReadOnly,
  onCopyUrl,
}) => {
  const items = Array.isArray(rawItems) ? rawItems : [];
  const renderActionButtons = (item) => (
    <div className="flex items-center justify-end gap-2 p-3 bg-gray-50">
      {onCopyUrl && (item.repeatable || item.slug) && (
        <button
          onClick={() => onCopyUrl(item)}
          className="p-2 text-[#1086a4] hover:text-[#054581] transition-colors rounded-full hover:bg-white"
          aria-label="Copy student URL"
        >
          <Link2 size={20} />
        </button>
      )}
      <button
        onClick={() => onView(item)}
        className="p-2 text-[#1086a4] hover:text-[#054581] transition-colors rounded-full hover:bg-white"
        aria-label="View"
      >
        <Eye size={20} />
      </button>
      {!isReadOnly && (
        <>
          <button
            onClick={() => onEdit(item)}
            className="p-2 text-[#1086a4] hover:text-[#054581] transition-colors rounded-full hover:bg-white"
            aria-label="Edit"
          >
            <Edit2 size={20} />
          </button>
          <button
            onClick={() => onDelete(item)}
            className="p-2 text-red-500 hover:text-red-700 transition-colors rounded-full hover:bg-white"
            aria-label="Delete"
          >
            <Trash2 size={20} />
          </button>
        </>
      )}
    </div>
  );
  const renderLessonCard = (item) => (
    <div key={item._id} className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
      <div className="p-4 border-b border-gray-100">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="bg-[#1086a4] bg-opacity-10 p-2 rounded-lg">
              <FileText className="w-5 h-5 text-[#1086a4]" />
            </div>
            <h3 className="font-semibold text-gray-900 text-lg">{item.title}</h3>
          </div>
        </div>
        {item.description && (
          <p className="text-sm text-gray-600 mt-3 line-clamp-2">
            {item.description}
          </p>
        )}
        {item.tags && item.tags.length > 0 && (
          <div className="mt-3">
            <div className="flex flex-wrap gap-1">
              {item.tags.map(tag => (
                <span
                  key={tag._id}
                  className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-[#1086a4] bg-opacity-10 text-[#1086a4]"
                >
                  <Tag className="w-3 h-3 mr-1" />
                  {tag.title}
                </span>
              ))}
            </div>
          </div>
        )}
      </div>

      {renderActionButtons(item)}
    </div>
  );

  const renderCourseCard = (item) => (
    <div key={item._id} className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
      <div className="flex items-center p-4 border-b border-gray-100">
        <div className="flex-shrink-0">
          {item.thumbnail ? (
            <img 
              src={getThumbnailUrl(item.thumbnail)}
              alt={item.title}
              className="w-20 h-20 object-cover rounded-lg"
              onError={(e) => {
                e.target.style.display = 'none';
                e.target.parentElement.innerHTML = '<div class="w-20 h-20 bg-gray-100 rounded-lg flex items-center justify-center"><ImageIcon class="w-8 h-8 text-gray-400" /></div>';
              }}
            />
          ) : (
            <div className="w-20 h-20 bg-gray-100 rounded-lg flex items-center justify-center">
              <ImageIcon className="w-8 h-8 text-gray-400" />
            </div>
          )}
        </div>
        <div className="ml-4 flex-1">
          <h3 className="font-semibold text-gray-900 text-lg">{item.title}</h3>
          <div className="flex items-center gap-3 mt-1 text-sm text-[#1086a4]">
            <span className="flex items-center gap-1">
              <FileText className="w-4 h-4" />
              {item.lessons?.length || 0}
            </span>
            <span className="flex items-center gap-1">
              <ClipboardList className="w-4 h-4" />
              {item.submissionForms?.length || 0}
            </span>
          </div>
        </div>
      </div>
      
      {item.tags && item.tags.length > 0 && (
        <div className="px-4 pb-2">
          <div className="flex flex-wrap gap-1">
            {item.tags.map(tag => (
              <span
                key={tag._id}
                className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-[#1086a4] bg-opacity-10 text-[#1086a4]"
              >
                <Tag className="w-3 h-3 mr-1" />
                {tag.title}
              </span>
            ))}
          </div>
        </div>
      )}

      {renderActionButtons(item)}
    </div>
  );

  const renderEventCard = (item) => (
    <div key={item._id} className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
      <div className="flex items-center p-4 border-b border-gray-100">
        <div className="flex-shrink-0">
          {item.thumbnail ? (
            <img 
              src={getThumbnailUrl(item.thumbnail)}
              alt={item.title}
              className="w-20 h-20 object-cover rounded-lg"
              onError={(e) => {
                e.target.style.display = 'none';
                e.target.parentElement.innerHTML = '<div class="w-20 h-20 bg-gray-100 rounded-lg flex items-center justify-center"><ImageIcon class="w-8 h-8 text-gray-400" /></div>';
              }}
            />
          ) : (
            <div className="w-20 h-20 bg-gray-100 rounded-lg flex items-center justify-center">
              <ImageIcon className="w-8 h-8 text-gray-400" />
            </div>
          )}
        </div>
        <div className="ml-4 flex-1">
          <div className="flex items-center justify-between">
            <h3 className="font-semibold text-gray-900 text-lg">{item.title}</h3>
            <span className={`px-2 py-1 rounded-full text-xs font-medium ${
              item.type === 'event' ? 'bg-blue-100 text-blue-800' : 'bg-green-100 text-green-800'
            }`}>
              {item.type === 'event' ? 'Event' : 'Information'}
            </span>
          </div>
          <div className="flex flex-col gap-1 mt-1">
            {item.type === 'event' && item.date && (
              <div className="flex items-center text-sm text-gray-600">
                <Calendar className="w-4 h-4 mr-1" />
                <span>{new Date(item.date).toLocaleDateString()}</span>
              </div>
            )}
            <div className="flex items-center text-sm text-[#1086a4]">
              <Users className="w-4 h-4 mr-1" />
              <span>{item.program?.title || 'No Program'}</span>
            </div>
          </div>
        </div>
      </div>
      
      {item.tags && item.tags.length > 0 && (
        <div className="px-4 pb-2">
          <div className="flex flex-wrap gap-1">
            {item.tags.map(tag => (
              <span
                key={tag._id}
                className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-[#1086a4] bg-opacity-10 text-[#1086a4]"
              >
                <Tag className="w-3 h-3 mr-1" />
                {tag.title}
              </span>
            ))}
          </div>
        </div>
      )}

      {renderActionButtons(item)}
    </div>
  );

  const renderQuestionCard = (item) => (
    <div key={item._id} className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
      <div className="p-4 border-b border-gray-100">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="bg-[#1086a4] bg-opacity-10 p-2 rounded-lg">
              <HelpCircle className="w-5 h-5 text-[#1086a4]" />
            </div>
            <h3 className="font-semibold text-gray-900 text-lg">{item.text}</h3>
          </div>
        </div>
        <div className="mt-3 flex items-center text-sm text-[#1086a4]">
          <Book className="w-4 h-4 mr-1" />
          <span>{item.options?.length || 0} Options</span>
        </div>
        {item.tags && item.tags.length > 0 && (
          <div className="mt-3">
            <div className="flex flex-wrap gap-1">
              {item.tags.map(tag => (
                <span
                  key={tag._id}
                  className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-[#1086a4] bg-opacity-10 text-[#1086a4]"
                >
                  <Tag className="w-3 h-3 mr-1" />
                  {tag.title}
                </span>
              ))}
            </div>
          </div>
        )}
      </div>

      {renderActionButtons(item)}
    </div>
  );

  const renderSubmissionFormCard = (item) => (
    <div key={item._id} className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
      <div className="p-4 border-b border-gray-100">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="bg-[#1086a4] bg-opacity-10 p-2 rounded-lg">
              <ClipboardList className="w-5 h-5 text-[#1086a4]" />
            </div>
            <h3 className="font-semibold text-gray-900 text-lg">{item.title}</h3>
          </div>
        </div>
                 <div className="mt-3 space-y-2">
           <div className="flex items-center text-sm text-gray-600">
             <span className="bg-gray-100 px-2 py-1 rounded text-xs">
               Course: {item.courseId?.title || 'No Course Assigned'}
             </span>
           </div>
           <div className="flex items-center text-sm text-[#1086a4]">
             <span className="bg-gray-100 px-2 py-1 rounded text-xs">
               {(() => {
                 const requirements = [];
                 if (item.linkRequired) requirements.push('Link');
                 if (item.commentRequired) requirements.push('Comment');
                 if (item.achievementImageRequired) requirements.push('Achievement Image');
                 return requirements.length > 0 ? requirements.join(' + ') : 'None';
               })()}
             </span>
           </div>
           <div className="flex items-center text-sm">
             <span className={`px-2 py-1 rounded text-xs font-medium ${
               item.automaticApproval ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
             }`}>
               Auto Approval: {item.automaticApproval ? 'Yes' : 'No'}
             </span>
           </div>
         </div>
      </div>

      {item.description && (
        <div className="p-4">
          <p className="text-sm text-gray-600 line-clamp-2">
            {item.description}
          </p>
        </div>
      )}

      {renderActionButtons(item)}
    </div>
  );

  const renderSubmissionCard = (item) => (
    <div key={item._id} className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
      <div className="p-4 border-b border-gray-100">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="bg-[#1086a4] bg-opacity-10 p-2 rounded-lg">
              <FileText className="w-5 h-5 text-[#1086a4]" />
            </div>
            <h3 className="font-semibold text-gray-900 text-lg">
              {item.userId?.name || 'Unknown'} - {item.submissionFormId?.title || 'Unknown Form'}
            </h3>
          </div>
        </div>
                 <div className="mt-3 space-y-2">
           <div className="flex items-center text-sm text-gray-600">
             <span className="bg-gray-100 px-2 py-1 rounded text-xs">
               Form: {item.submissionFormId?.title || 'Unknown Form'}
             </span>
           </div>
           <div className="flex items-center justify-between text-sm">
             <span className="bg-gray-100 px-2 py-1 rounded text-xs text-gray-600">
               Submitted At: {new Date(item.submittedAt || item.createdAt).toLocaleDateString()}
             </span>
             <span className={`px-2 py-1 rounded-full text-xs font-medium ${
               item.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
               item.status === 'approved' ? 'bg-green-100 text-green-800' :
               item.status === 'more_info_needed' ? 'bg-amber-100 text-amber-800' :
               'bg-red-100 text-red-800'
             }`}>
               {item.status === 'more_info_needed' ? 'More Info Needed' : item.status}
             </span>
           </div>
         </div>
      </div>

      {renderActionButtons(item)}
    </div>
  );

  const renderPathCard = (item) => (
    <div key={item._id} className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
      <div className="p-4 border-b border-gray-100">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="bg-[#1086a4] bg-opacity-10 p-2 rounded-lg">
              <GitFork className="w-5 h-5 text-[#1086a4]" />
            </div>
            <h3 className="font-semibold text-gray-900 text-lg">{item.title}</h3>
          </div>
        </div>
        <div className="mt-3 space-y-2">
          <div className="flex items-center text-sm text-gray-600">
            <span className="bg-gray-100 px-2 py-1 rounded">
              Score Range: {item.rangeMin} - {item.rangeMax}
            </span>
          </div>
          <div className="flex items-center text-sm text-[#1086a4]">
            <Book className="w-4 h-4 mr-1" />
            <span>{item.courses?.length || 0} Courses</span>
          </div>
        </div>
      </div>
      
      {item.tags && item.tags.length > 0 && (
        <div className="px-4 pb-2">
          <div className="flex flex-wrap gap-1">
            {item.tags.map(tag => (
              <span
                key={tag._id}
                className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-[#1086a4] bg-opacity-10 text-[#1086a4]"
              >
                <Tag className="w-3 h-3 mr-1" />
                {tag.title}
              </span>
            ))}
          </div>
        </div>
      )}

      {renderActionButtons(item)}
    </div>
  );

  const renderTermsConditionCard = (item) => (
    <div key={item._id} className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
      <div className="p-4 border-b border-gray-100">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="bg-[#1086a4] bg-opacity-10 p-2 rounded-lg">
              <Shield className="w-5 h-5 text-[#1086a4]" />
            </div>
            <h3 className="font-semibold text-gray-900 text-lg">{item.title}</h3>
          </div>
        </div>
        <div className="mt-3">
          <div className="flex items-center text-sm text-gray-600">
            <span className="bg-gray-100 px-2 py-1 rounded text-xs">
              Program: {item.program?.title || 'No program assigned'}
            </span>
          </div>
        </div>
      </div>

      <div className="p-4">
        <p className="text-sm text-gray-600 line-clamp-3">
          {item.contentPreview || truncateText(item.content, 120)}
        </p>
      </div>

      {renderActionButtons(item)}
    </div>
  );

  const renderTagCard = (item) => (
    <div key={item._id} className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
      <div className="p-4 border-b border-gray-100">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="bg-[#1086a4] bg-opacity-10 p-2 rounded-lg">
              <Tag className="w-5 h-5 text-[#1086a4]" />
            </div>
            <h3 className="font-semibold text-gray-900 text-lg">{item.title}</h3>
          </div>
        </div>
        <div className="mt-3">
          <div className="flex items-center text-sm text-gray-600">
            <span className="bg-gray-100 px-2 py-1 rounded text-xs">
              Created by: {item.createdBy}
            </span>
          </div>
        </div>
      </div>

      {renderActionButtons(item)}
    </div>
  );

  return (
    <div className="space-y-4">
             {items.map(item => {
         switch (activeTab) {
           case 'Lessons':
             return renderLessonCard(item);
           case 'Courses':
             return renderCourseCard(item);
           case 'Events & Information':
             return renderEventCard(item);
           case 'Questions':
             return renderQuestionCard(item);
           case 'Submission Forms':
             return renderSubmissionFormCard(item);
           case 'Submissions':
             return renderSubmissionCard(item);
           case 'Paths':
             return renderPathCard(item);
           case 'Terms & Conditions':
             return renderTermsConditionCard(item);
           case 'Tags':
             return renderTagCard(item);
           default:
             return null;
         }
       })}
    </div>
  );
};

export default MobileView; 